import React, { useState } from 'react';
import { smartCase } from '../../utils/smartCase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faChevronDown, 
    faChevronUp,
    faCheckCircle,
    faTimesCircle,
    faUser,
    faCalendar
} from '@fortawesome/free-solid-svg-icons';
import './RFCHistoryModal.css';

/**
 * RFCHistoryModal Component
 * 
 * Displays reviewed (approved/rejected) RFC history for a CRM profile.
 * All users with CRM access can view this history.
 * Shows field-level approval decisions and rejection reasons.
 * 
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: function - Callback to close the modal
 * - rfcHistory: array - Array of reviewed RFCs
 * - profileName: string - Name of the CRM profile
 */
const RFCHistoryModal = ({ isOpen, onClose, rfcHistory, profileName }) => {
    const historyArray = Array.isArray(rfcHistory) ? rfcHistory : (rfcHistory ? [rfcHistory] : []);
    
    const [expandedRFCs, setExpandedRFCs] = useState(() => {
        // Expand the first RFC by default
        return historyArray.length > 0 ? { [historyArray[0].id]: true } : {};
    });
    
    if (!isOpen || historyArray.length === 0) return null;
    
    // Toggle RFC expansion
    const toggleRFC = (rfcId) => {
        setExpandedRFCs(prev => ({
            ...prev,
            [rfcId]: !prev[rfcId]
        }));
    };
    
    // Sort RFCs by reviewed_at (newest first)
    const sortedRFCs = [...historyArray].sort((a, b) => {
        const dateA = new Date(a.reviewed_at || a.created_at);
        const dateB = new Date(b.reviewed_at || b.created_at);
        return dateB - dateA;
    });
    
    // Format date helper
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };
    
    // Format field name helper
    const formatFieldName = (fieldName) => {
        return fieldName
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };
    
    // Helper function to safely format nested values
    const formatNestedValue = (val) => {
        if (val === null || val === undefined || val === '') {
            return '(empty)';
        }
        if (typeof val === 'boolean') {
            return val ? 'Yes' : 'No';
        }
        if (typeof val === 'object') {
            // If it's an object, stringify it nicely
            if (val.formatted_address) {
                return val.formatted_address;
            }
            return JSON.stringify(val, null, 2);
        }
        return String(val);
    };
    
    // Format value helper - returns JSX for proper display
    const formatValue = (value) => {
        if (value === null || value === undefined || value === '') {
            return <span className="rfc-empty-value">(empty)</span>;
        }
        
        if (typeof value === 'boolean') {
            return value ? 'Yes' : 'No';
        }
        
        if (Array.isArray(value)) {
            if (value.length === 0) {
                return <span className="rfc-empty-value">(empty)</span>;
            }
            
            // Check if array contains objects (like banking details, contacts, etc.)
            if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                return (
                    <div className="rfc-object-list">
                        {value.map((item, idx) => (
                            <div key={idx} className="rfc-object-item">
                                <strong>Item {idx + 1}:</strong>
                                <ul className="rfc-object-fields">
                                    {Object.entries(item).map(([key, val]) => (
                                        <li key={key}>
                                            <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                                            <span className="rfc-object-value">{formatNestedValue(val)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                );
            }
            
            return value.join(', ') || <span className="rfc-empty-value">(empty)</span>;
        }
        
        if (typeof value === 'object') {
            // For single objects, format as structured list
            return (
                <ul className="rfc-object-fields">
                    {Object.entries(value).map(([key, val]) => (
                        <li key={key}>
                            <span className="rfc-object-key">{formatFieldName(key)}:</span>{' '}
                            <span className="rfc-object-value">{formatNestedValue(val)}</span>
                        </li>
                    ))}
                </ul>
            );
        }
        
        return String(value);
    };
    
    // Get status badge class
    const getStatusBadgeClass = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return 'rfc-history-status-approved';
            case 'rejected':
                return 'rfc-history-status-rejected';
            default:
                return 'rfc-history-status-pending';
        }
    };
    
    // Get decision badge class
    const getDecisionBadgeClass = (decision) => {
        switch (decision?.toLowerCase()) {
            case 'approved':
                return 'approved';
            case 'rejected':
                return 'rejected';
            default:
                return '';
        }
    };
    
    // Handle overlay click (close modal when clicking outside)
    const handleOverlayClick = (e) => {
        if (e.target.className === 'rfc-history-overlay') {
            onClose();
        }
    };
    
    return (
        <div className="rfc-history-overlay" onClick={handleOverlayClick}>
            <div className="rfc-history-modal">
                {/* Header */}
                <div className="rfc-history-header">
                    <h3>RFC History</h3>
                    <p className="rfc-history-profile-name">{profileName}</p>
                    <button 
                        className="rfc-history-close-button" 
                        onClick={onClose}
                        aria-label="Close RFC History"
                        type="button"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                
                {/* Body - RFC List */}
                <div className="rfc-history-body">
                    <div className="rfc-history-list-container">
                        {sortedRFCs.map((rfc, index) => {
                            const rfcNumber = sortedRFCs.length - index;
                            const isExpanded = expandedRFCs[rfc.id];
                            const changesData = rfc.changes_data || {};
                            const fieldDecisions = rfc.field_decisions || {};
                            const changeCount = Object.keys(changesData).length;
                            
                            return (
                                <div key={rfc.id} className="rfc-history-card">
                                    {/* RFC Card Header (Clickable) */}
                                    <div 
                                        className="rfc-history-card-header"
                                        onClick={() => toggleRFC(rfc.id)}
                                    >
                                        <div className="rfc-history-card-header-left">
                                            <span className="rfc-history-card-toggle-icon">
                                                <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                            </span>
                                            <div className="rfc-history-card-title">
                                                <h4>RFC #{rfcNumber}</h4>
                                                <span className="rfc-history-card-date">
                                                    Reviewed {formatDate(rfc.reviewed_at)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="rfc-history-card-header-right">
                                            <span className={`rfc-history-status-badge ${getStatusBadgeClass(rfc.status)}`}>
                                                {rfc.status === 'approved' && <FontAwesomeIcon icon={faCheckCircle} />}
                                                {rfc.status === 'rejected' && <FontAwesomeIcon icon={faTimesCircle} />}
                                                {rfc.status ? rfc.status.charAt(0).toUpperCase() + rfc.status.slice(1) : 'Unknown'}
                                            </span>
                                            <span className="rfc-history-change-count">
                                                {changeCount} change{changeCount !== 1 ? 's' : ''}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    {/* RFC Card Body (Expandable) */}
                                    {isExpanded && (
                                        <div className="rfc-history-card-body">
                                            {/* Request Info */}
                                            <div className="rfc-history-card-meta">
                                                <div className="rfc-history-meta-item">
                                                    <FontAwesomeIcon icon={faUser} />
                                                    <span>
                                                        <strong>Requested by:</strong> {rfc.requested_by_name || 'Unknown'}
                                                    </span>
                                                </div>
                                                <div className="rfc-history-meta-item">
                                                    <FontAwesomeIcon icon={faUser} />
                                                    <span>
                                                        <strong>Reviewed by:</strong> {rfc.reviewed_by_name || 'Unknown'}
                                                    </span>
                                                </div>
                                                <div className="rfc-history-meta-item">
                                                    <FontAwesomeIcon icon={faCalendar} />
                                                    <span>
                                                        <strong>Submitted:</strong> {formatDate(rfc.created_at)}
                                                    </span>
                                                </div>
                                                {rfc.requested_by_employee_id && (
                                                    <div className="rfc-history-meta-item">
                                                        <span>
                                                            <strong>Employee ID:</strong> {rfc.requested_by_employee_id}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Changes Section */}
                                            <div className="rfc-history-changes-section">
                                                <h5>Field Changes & Decisions:</h5>
                                                <div className="rfc-history-changes-list">
                                                    {Object.entries(changesData).map(([fieldName, change]) => {
                                                        const decision = fieldDecisions[fieldName];
                                                        const oldValue = change.old;
                                                        const newValue = change.new;
                                                        
                                                        return (
                                                            <div key={fieldName} className="rfc-history-change-row">
                                                                <div className="rfc-history-change-field-name">
                                                                    {smartCase(formatFieldName(fieldName))}
                                                                </div>
                                                                <div className="rfc-history-change-values">
                                                                    <div className="rfc-history-value-old">
                                                                        <span className="rfc-history-value-label">Old:</span>
                                                                        <span className="rfc-history-value-content">{formatValue(oldValue)}</span>
                                                                    </div>
                                                                    <div className="rfc-history-value-arrow">→</div>
                                                                    <div className="rfc-history-value-new">
                                                                        <span className="rfc-history-value-label">New:</span>
                                                                        <span className="rfc-history-value-content">{formatValue(newValue)}</span>
                                                                    </div>
                                                                </div>
                                                                
                                                                {/* Decision Badge */}
                                                                {decision && (
                                                                    <div className="rfc-history-field-decision-badge">
                                                                        <span className={`decision-badge ${getDecisionBadgeClass(decision.decision)}`}>
                                                                            <FontAwesomeIcon icon={decision.decision === 'approved' ? faCheckCircle : faTimesCircle} />
                                                                            {decision.decision === 'approved' ? 'Approved' : 'Rejected'}
                                                                        </span>
                                                                        {decision.rejection_reason && (
                                                                            <span className="rejection-reason">
                                                                                Reason: {decision.rejection_reason}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            
                                            {/* Review Notes (if any) */}
                                            {rfc.review_notes && (
                                                <div className="rfc-history-card-review">
                                                    <h5>Review Notes:</h5>
                                                    <p>{rfc.review_notes}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                
                {/* Footer */}
                <div className="rfc-history-footer">
                    <button 
                        className="rfc-history-close-button-footer" 
                        onClick={onClose}
                        type="button"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RFCHistoryModal;

